import { theme as antdTheme } from 'antd';
import type { ThemeConfig } from 'antd';
import { Azure, DarkTheme, Gold, LightTheme, Shadow } from '@barahpay/shared';

export type ThemeMode = 'light' | 'dark';

/**
 * The Barah Plus design system expressed as an Ant Design theme.
 *
 * Barah Plus (MeezanFrontEnd) is Tailwind; this portal is Ant Design. Rather than rebuild twenty-five
 * screens on a second component library, every seed and component token below is set to the value the
 * matching Barah Plus component renders — the gold ramp, the navy-based neutrals, `rounded-xl` controls,
 * the soft-shadow cards, the uppercase table headers — so an antd Button, Table or Select reads as the
 * Barah Plus one. What tokens cannot express (the gradient primary button, the table frame) lives in
 * styles.css.
 */
export function buildTheme(mode: ThemeMode): ThemeConfig {
  const dark = mode === 'dark';
  const surfaces = dark ? DarkTheme : LightTheme;

  return {
    algorithm: dark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,

    token: {
      colorPrimary: Gold[500],
      colorLink: dark ? Gold[400] : Gold[600],
      colorLinkHover: dark ? Gold[300] : Gold[700],
      colorInfo: Azure[500],
      colorSuccess: '#22C55E',
      colorWarning: '#F59E0B',
      colorError: '#EF4444',

      colorTextBase: surfaces.textPrimary,
      colorTextHeading: surfaces.textHeading,
      colorTextSecondary: surfaces.textSecondary,
      colorTextTertiary: surfaces.textMuted,
      colorTextQuaternary: dark ? '#5A6A7E' : '#C4CBD8',

      colorBgBase: surfaces.surface,
      colorBgContainer: surfaces.surface,
      colorBgElevated: surfaces.surface,
      colorBgLayout: surfaces.background,
      colorFillAlter: dark ? 'rgba(42, 58, 78, 0.35)' : '#FAFBFD',
      colorFillTertiary: dark ? 'rgba(42, 58, 78, 0.5)' : '#F0F2F7',
      colorBorder: surfaces.border,
      colorBorderSecondary: surfaces.borderLight,
      colorSplit: surfaces.borderLight,

      // Follows the document direction: Inter for English, Cairo for Arabic (see brand.css).
      fontFamily: 'var(--bp-font)',
      fontSize: 14,
      lineHeight: 1.6,

      // Barah Plus radii: rounded-md 8, rounded-lg 12, rounded-xl 16.
      borderRadius: 12,
      borderRadiusLG: 16,
      borderRadiusSM: 8,
      borderRadiusXS: 6,

      controlHeight: 40,
      controlHeightLG: 44,
      controlHeightSM: 32,
      controlOutline: 'rgba(212, 160, 60, 0.2)',
      controlOutlineWidth: 3,
      wireframe: false,

      boxShadow: Shadow.card,
      boxShadowSecondary: dark
        ? '0 2px 12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(42, 58, 78, 0.6)'
        : '0 2px 12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(226, 230, 238, 0.6)',
      boxShadowTertiary: dark ? '0 3px 8px rgba(0, 0, 0, 0.3)' : Shadow.soft,

      motionDurationMid: '0.2s',
    },

    components: {
      Layout: {
        bodyBg: 'transparent',
        headerBg: 'transparent',
        siderBg: 'transparent',
        footerBg: 'transparent',
      },

      Button: {
        borderRadius: 14,
        borderRadiusLG: 16,
        borderRadiusSM: 10,
        fontWeight: 500,
        paddingInline: 16,
        primaryShadow: Shadow.primary,
        primaryColor: '#FFFFFF',
        dangerShadow: '0 10px 15px -3px rgba(239, 68, 68, 0.25)',
        defaultShadow: 'none',
        defaultBorderColor: surfaces.border,
        defaultColor: dark ? '#E2E6EE' : '#2A3A4E',
        defaultHoverBg: dark ? 'rgba(42, 58, 78, 0.5)' : '#FAFBFD',
        defaultHoverBorderColor: dark ? '#5A6A7E' : '#C4CBD8',
        defaultHoverColor: dark ? '#FFFFFF' : '#1A2332',
        textHoverBg: dark ? 'rgba(42, 58, 78, 0.6)' : '#F0F2F7',
      },

      Input: {
        paddingBlock: 8,
        paddingInline: 14,
        activeBorderColor: Gold[500],
        hoverBorderColor: dark ? '#5A6A7E' : '#C4CBD8',
        activeShadow: '0 0 0 3px rgba(212, 160, 60, 0.2)',
      },
      InputNumber: {
        activeBorderColor: Gold[500],
        hoverBorderColor: dark ? '#5A6A7E' : '#C4CBD8',
        activeShadow: '0 0 0 3px rgba(212, 160, 60, 0.2)',
      },
      Select: {
        activeBorderColor: Gold[500],
        hoverBorderColor: dark ? '#5A6A7E' : '#C4CBD8',
        activeOutlineColor: 'rgba(212, 160, 60, 0.2)',
        optionSelectedBg: dark ? 'rgba(212, 160, 60, 0.15)' : Gold[50],
        optionSelectedColor: dark ? Gold[300] : Gold[700],
        optionActiveBg: dark ? 'rgba(42, 58, 78, 0.6)' : '#F0F2F7',
        multipleItemBg: dark ? 'rgba(42, 58, 78, 0.6)' : '#F0F2F7',
      },
      DatePicker: {
        activeBorderColor: Gold[500],
        hoverBorderColor: dark ? '#5A6A7E' : '#C4CBD8',
        activeShadow: '0 0 0 3px rgba(212, 160, 60, 0.2)',
        cellActiveWithRangeBg: dark ? 'rgba(212, 160, 60, 0.15)' : Gold[50],
      },

      Card: {
        borderRadiusLG: 16,
        paddingLG: 20,
        headerFontSize: 14,
        headerFontSizeSM: 14,
        headerHeight: 52,
        headerHeightSM: 44,
        headerBg: 'transparent',
      },

      Table: {
        headerBg: dark ? 'rgba(42, 58, 78, 0.35)' : 'rgba(250, 251, 253, 0.8)',
        headerColor: surfaces.textSecondary,
        headerSplitColor: 'transparent',
        headerBorderRadius: 16,
        headerSortActiveBg: dark ? 'rgba(42, 58, 78, 0.55)' : '#F0F2F7',
        headerSortHoverBg: dark ? 'rgba(42, 58, 78, 0.55)' : '#F0F2F7',
        bodySortBg: 'transparent',
        rowHoverBg: dark ? 'rgba(42, 58, 78, 0.3)' : 'rgba(250, 251, 253, 0.8)',
        rowSelectedBg: dark ? 'rgba(212, 160, 60, 0.12)' : 'rgba(253, 248, 236, 0.6)',
        rowSelectedHoverBg: dark ? 'rgba(212, 160, 60, 0.18)' : Gold[50],
        borderColor: dark ? 'rgba(42, 58, 78, 0.6)' : '#F0F2F7',
        cellPaddingBlock: 14,
        cellPaddingInline: 16,
        cellPaddingBlockMD: 12,
        cellPaddingInlineMD: 14,
        cellPaddingBlockSM: 11,
        cellPaddingInlineSM: 12,
        cellFontSize: 14,
        cellFontSizeSM: 13,
        footerBg: 'transparent',
        expandIconBg: surfaces.surface,
      },

      Tag: {
        defaultBg: dark ? '#2A3A4E' : '#F0F2F7',
        defaultColor: dark ? '#C4CBD8' : '#2A3A4E',
      },

      Tabs: {
        itemColor: surfaces.textSecondary,
        itemHoverColor: dark ? '#E2E6EE' : '#1A2332',
        itemSelectedColor: dark ? Gold[400] : Gold[600],
        itemActiveColor: dark ? Gold[400] : Gold[600],
        inkBarColor: Gold[500],
        titleFontSize: 14,
        horizontalItemPadding: '10px 12px',
        horizontalItemGutter: 4,
        horizontalMargin: '0 0 20px 0',
        cardBg: dark ? 'rgba(42, 58, 78, 0.35)' : '#FAFBFD',
      },

      Segmented: {
        trackBg: dark ? '#0B1426' : '#F0F2F7',
        trackPadding: 4,
        itemColor: surfaces.textSecondary,
        itemHoverColor: surfaces.textPrimary,
        itemHoverBg: 'transparent',
        itemSelectedBg: surfaces.surface,
        itemSelectedColor: dark ? Gold[400] : Gold[600],
        borderRadius: 12,
        borderRadiusSM: 8,
      },

      Menu: {
        itemBorderRadius: 12,
        itemSelectedBg: dark ? 'rgba(212, 160, 60, 0.15)' : Gold[50],
        itemSelectedColor: dark ? Gold[300] : Gold[700],
        itemHoverBg: dark ? '#2A3A4E' : '#F0F2F7',
      },

      Dropdown: {
        borderRadiusLG: 16,
        borderRadiusSM: 12,
        paddingBlock: 9,
        controlItemBgHover: dark ? '#2A3A4E' : '#F0F2F7',
      },

      Modal: {
        borderRadiusLG: 24,
        titleFontSize: 18,
        headerBg: 'transparent',
        contentBg: surfaces.surface,
        footerBg: 'transparent',
      },

      Drawer: {
        paddingLG: 24,
      },

      Pagination: {
        itemActiveBg: 'transparent',
        itemSize: 32,
        borderRadius: 12,
      },

      Descriptions: {
        labelBg: dark ? 'rgba(42, 58, 78, 0.35)' : '#FAFBFD',
        titleMarginBottom: 12,
        itemPaddingBottom: 12,
        colorTextLabel: surfaces.textSecondary,
      },

      Statistic: {
        titleFontSize: 12,
        contentFontSize: 24,
      },

      Alert: {
        borderRadiusLG: 16,
        withDescriptionPadding: '14px 16px',
      },

      Badge: { textFontSize: 11 },

      Checkbox: { borderRadiusSM: 6 },

      Switch: { colorPrimary: Gold[500], colorPrimaryHover: Gold[600] },

      Tooltip: { borderRadius: 10 },

      Popover: { borderRadiusLG: 16 },

      Timeline: { dotBg: surfaces.surface },

      Result: { titleFontSize: 20 },

      Typography: { titleMarginBottom: '0.4em', titleMarginTop: '0.8em' },
    },
  };
}
